import {
  createEffect,
  createSignal,
  For,
  Match,
  onMount,
  Switch,
} from "solid-js";
import { A, useNavigate } from "@solidjs/router";
import { debounce } from "@solid-primitives/scheduled";

import { BackIcon } from "../components/icons";
import { City, citySearch, loadCitySearchIndex } from "../services/city-search";
import { cacheRequest } from "../services/cache";
import { WIFI_NETWORK_KIND } from "../const";
import { eventStore } from "../services/stores";
import { wifiSearch, WifiSearchItem } from "../services/wifi-search";

function LoadingSpinner() {
  return (
    <div class="flex justify-center items-center h-32">
      <span class="loading loading-spinner loading-lg text-primary"></span>
    </div>
  );
}

function CityResults(props: { query: string }) {
  const [results, setResults] = createSignal<City[] | null | undefined>(
    undefined,
  );

  const search = debounce(() => {
    if (props.query) {
      const results = citySearch.search(props.query, { limit: 20 });
      setResults(results.map((r) => r.item));
    } else {
      setResults([]);
    }
  }, 500);

  onMount(() => {
    loadCitySearchIndex();
  });

  createEffect(() => {
    if (props.query.length >= 2) {
      search();
      setResults(null);
    } else {
      setResults(undefined);
    }
  });

  return (
    <For
      each={results()}
      fallback={
        <Switch
          fallback={
            <div class="flex justify-center items-center h-32">
              <p class="text-lg text-base-content/60">Search for city</p>
            </div>
          }
        >
          <Match when={results() === null}>
            <LoadingSpinner />
          </Match>
          <Match when={results()?.length === 0}>
            <div class="flex justify-center items-center h-32">
              <p class="text-lg text-base-content/60">No cities found</p>
            </div>
          </Match>
        </Switch>
      }
    >
      {(city) => (
        <li class="card bg-base-100 shadow mb-2">
          <div class="card-body p-4">
            <A
              href="/"
              class="btn btn-primary btn-sm self-end"
              state={{ center: { lat: city.lat, lng: city.lng } }}
            >
              View
            </A>
            <div class="card-title">
              {city.name} ({city.country})
            </div>
            <div class="text-sm text-base-content/70">
              Lat: {city.lat}, Lng: {city.lng}
            </div>
          </div>
        </li>
      )}
    </For>
  );
}

// keep this out of the component so its global
let loadedFromCache = false;

function WifiResults(props: { query: string }) {
  const [results, setResults] = createSignal<
    WifiSearchItem[] | null | undefined
  >(undefined);

  const search = debounce(() => {
    if (props.query) {
      const results = wifiSearch.search(props.query, { limit: 20 });
      setResults(results.map((r) => r.item));
    } else {
      setResults([]);
    }
  }, 500);

  createEffect(() => {
    if (props.query.length >= 2) {
      search();
      setResults(null);
    } else {
      setResults(undefined);
    }
  });

  return (
    <For
      each={results()}
      fallback={
        <Switch
          fallback={
            <div class="flex justify-center items-center h-32">
              <p class="text-lg text-base-content/60">
                Search for WiFi networks
              </p>
            </div>
          }
        >
          <Match when={results() === null}>
            <LoadingSpinner />
          </Match>
          <Match when={results()?.length === 0}>
            <div class="flex justify-center items-center h-32">
              <p class="text-lg text-base-content/60">No networks found</p>
            </div>
          </Match>
        </Switch>
      }
    >
      {(network) => (
        <li class="card bg-base-100 shadow mb-2">
          <div class="card-body p-4">
            <A
              href={
                network.network.naddr ? `/wifi/${network.network.naddr}` : "#"
              }
              class="btn btn-primary btn-sm self-end"
            >
              View
            </A>
            <div class="card-title">{network.ssid}</div>
            <div class="text-sm text-base-content/70">{network.about}</div>
          </div>
        </li>
      )}
    </For>
  );
}

function SearchView() {
  const navigate = useNavigate();

  const [query, setQuery] = createSignal("");
  const [searchMode, setSearchMode] = createSignal<"cities" | "networks">(
    "cities",
  );

  // load all cached networks when mode is switched
  createEffect(() => {
    if (searchMode() === "networks" && !loadedFromCache) {
      console.log("Loading all wifi networks from cache");

      loadedFromCache = true;
      cacheRequest([{ kinds: [WIFI_NETWORK_KIND] }]).subscribe((event) =>
        eventStore.add(event),
      );
    }
  });

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    setQuery(target.value);
  };

  return (
    <>
      <main class="overflow-y-auto bg-base-200 p-2 flex flex-grow flex-col-reverse">
        {searchMode() === "cities" ? (
          <CityResults query={query()} />
        ) : (
          <WifiResults query={query()} />
        )}
      </main>

      <footer class="flex flex-col gap-2 p-2 pb-safe-or-2">
        <div class="join">
          <button
            class={`btn join-item ${searchMode() === "cities" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setSearchMode("cities")}
          >
            Cities
          </button>
          <button
            class={`btn join-item ${searchMode() === "networks" ? "btn-primary" : "btn-outline"}`}
            onClick={() => setSearchMode("networks")}
          >
            Networks
          </button>
        </div>

        <div class="flex items-center gap-2">
          <button
            onClick={() => navigate(-1)}
            class="btn btn-primary btn-square"
            aria-label="back"
          >
            <BackIcon />
          </button>
          <input
            type="text"
            class="input input-bordered flex-grow"
            placeholder={`Search for a ${searchMode()}...`}
            value={query()}
            on:input={handleInputChange}
            autofocus
          />
        </div>
      </footer>
    </>
  );
}

export default SearchView;
